import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Hash,
  Lock,
  RefreshCw,
  Save,
  ToggleLeft,
  ToggleRight,
  Trash2,
  X,
} from 'lucide-react';
import MainService from '../../services/MainService';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';

const LIST_TAKE = 10000;

const COUNTER_COLUMNS = [
  { field: 'area', title: 'Area', hideable: true },
  { field: 'date', title: 'Date', hideable: true },
  { field: 'lastnumber', title: 'Last Number', hideable: true },
  { field: 'status', title: 'Status', hideable: true },
  { field: 'actions', title: 'Options', hideable: true, sortable: false, filterable: false, groupable: false, align: 'right' },
];

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const isSuccess = (response) => response === true || response === 'true' || response === 'Success';

const getCellValue = (row, field) => {
  switch (field) {
    case 'area':
      return row.Area?.Name || '';
    case 'date':
      return formatDate(row.Date);
    case 'lastnumber':
      return String(row.LastNumber ?? '');
    case 'status':
      return row.IsOpen ? 'Open' : 'Closed';
    default:
      return row[field] ?? '';
  }
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB');
};

const CounterNos = () => {
  const [areas, setAreas] = useState([]);
  const [rows, setRows] = useState([]);
  const [areaId, setAreaId] = useState('');
  const [typeFilter, setTypeFilter] = useState('Open');
  const [editedNumbers, setEditedNumbers] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rowActionId, setRowActionId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const columns = useMemo(() => COUNTER_COLUMNS, []);
  const dirtyRows = useMemo(
    () => rows.filter((row) => Object.prototype.hasOwnProperty.call(editedNumbers, row.Id)),
    [editedNumbers, rows]
  );

  const resolveCellValue = useCallback(
    (row, field) => {
      if (field === 'lastnumber') {
        return String(editedNumbers[row.Id] ?? row.LastNumber ?? '');
      }
      return getCellValue(row, field);
    },
    [editedNumbers]
  );

  const grid = useClientListGrid({ columns, rows, getCellValue: resolveCellValue });

  const loadAreas = useCallback(async () => {
    try {
      const response = await MainService.AreasInDropDown();
      setAreas(parseList(response));
    } catch (err) {
      console.error('Load counter areas error:', err);
      setAreas([]);
      setError('Failed to load areas');
    }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.CountersInList({
        Type: typeFilter,
        Skip: 0,
        Take: LIST_TAKE,
      });

      const list = parseList(response);
      setRows(list);
      setEditedNumbers({});
    } catch (err) {
      console.error('Load counters error:', err);
      setRows([]);
      setError('Failed to load counters');
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const changeFilter = (value) => {
    setTypeFilter(value);
  };

  const saveCounter = async () => {
    if (!areaId) {
      setError('Select Area');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const response = await MainService.SaveCounter({ AreaId: Number(areaId) });
      if (response === 'Success') {
        setSuccess('Success');
        setAreaId('');
        await loadRows();
      } else if (response === 'Failed') {
        setError('Error');
      } else {
        setError(String(response || 'Failed to save counter'));
      }
    } catch (err) {
      console.error('Save counter error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  const saveLastNo = async (row) => {
    const nextNumber = Number(editedNumbers[row.Id] ?? row.LastNumber) || 0;
    try {
      setRowActionId(row.Id);
      setError('');
      const response = await MainService.UpdateLastNoCounter({ ...row, LastNumber: nextNumber });
      if (isSuccess(response)) {
        setSuccess('Success');
        await loadRows();
      } else {
        setError('Network Error');
      }
    } catch (err) {
      console.error('Save counter last number error:', err);
      setError('Network Error');
    } finally {
      setRowActionId(null);
    }
  };

  const saveAll = async () => {
    if (dirtyRows.length === 0) {
      setError('No changes to save');
      return;
    }

    const payload = dirtyRows.map((row) => ({
      ...row,
      LastNumber: Number(editedNumbers[row.Id] ?? row.LastNumber) || 0,
    }));

    try {
      setSaving(true);
      setError('');
      const response = await MainService.SaveAllCounter(payload);
      if (isSuccess(response)) {
        setSuccess('Success');
        await loadRows();
      } else {
        setError(String(response || 'Failed to save changes'));
      }
    } catch (err) {
      console.error('Save all counters error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  const cancelChanges = () => {
    setEditedNumbers({});
  };

  const toggleCounter = async (row) => {
    try {
      setRowActionId(row.Id);
      setError('');
      const response = await MainService.CloseOpenCounter(row.Id);
      if (isSuccess(response)) {
        setSuccess('Success');
        await loadRows();
      } else {
        setError('Network Error');
      }
    } catch (err) {
      console.error('Open/close counter error:', err);
      setError('Network Error');
    } finally {
      setRowActionId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.Id) return;
    try {
      setSaving(true);
      setError('');
      const response = await MainService.DeleteCounter(deleteTarget.Id);
      if (isSuccess(response)) {
        setSuccess('Deleted');
        setDeleteTarget(null);
        await loadRows();
      } else {
        setError('Network Error');
      }
    } catch (err) {
      console.error('Delete counter error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  const setAllCounters = async (open) => {
    try {
      setSaving(true);
      setError('');
      const response = open ? await MainService.OpenAllCounter() : await MainService.CloseAllCounter();
      if (isSuccess(response)) {
        setSuccess('Success');
        await loadRows();
      } else {
        setError(String(response || 'Network Error'));
      }
    } catch (err) {
      console.error('Open/close all counters error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  const updateLastNumberDraft = (rowId, value) => {
    setEditedNumbers((prev) => ({ ...prev, [rowId]: value }));
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3 text-primary">
              <Hash className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Counter Nos</h1>
              <p className="mt-1 text-sm text-gray-500">Open area counters and maintain the last used store reference number.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={loadRows}
            disabled={loading || saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">Area</label>
            <select
              value={areaId}
              onChange={(event) => setAreaId(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Select Area</option>
              {areas.map((area) => (
                <option key={area.Id} value={area.Id}>
                  {area.Name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-2 lg:col-span-4">
            <button
              type="button"
              onClick={saveCounter}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
            <button
              type="button"
              onClick={() => setAllCounters(false)}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Lock className="h-4 w-4" />
              Close All
            </button>
            <button
              type="button"
              onClick={() => setAllCounters(true)}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <ToggleRight className="h-4 w-4" />
              Open All
            </button>
          </div>

          <div className="lg:col-span-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
            <select
              value={typeFilter}
              onChange={(event) => changeFilter(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="All">All</option>
              <option value="Open">Open</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Counters List</h2>
            <p className="text-sm text-gray-500">
              {grid.totalCount} counters
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={saveAll}
              disabled={saving || dirtyRows.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              Save All
            </button>
            <button
              type="button"
              onClick={cancelChanges}
              disabled={dirtyRows.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          </div>
        </div>

        <ClientListGrid
          columns={columns}
          grid={grid}
          loading={loading}
          loadingText="Loading counters..."
          emptyText="No counters found."
          minWidth="900px"
          rowKey={(row) => row.Id}
          getRowClassName={(row) =>
            Object.prototype.hasOwnProperty.call(editedNumbers, row.Id) ? 'bg-amber-50' : ''
          }
          renderCell={(row, column) => {
            const currentLastNumber = editedNumbers[row.Id] ?? row.LastNumber ?? 0;
            if (column.field === 'lastnumber') {
              return (
                <input
                  type="number"
                  value={currentLastNumber}
                  onChange={(event) => updateLastNumberDraft(row.Id, event.target.value)}
                  disabled={!row.IsOpen}
                  className="w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-gray-100 disabled:text-gray-500"
                />
              );
            }
            if (column.field === 'status') {
              return (
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${row.IsOpen ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                  {row.IsOpen ? 'Open' : 'Closed'}
                </span>
              );
            }
            if (column.field === 'actions') {
              return (
                <div className="inline-flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(row)}
                    disabled={rowActionId === row.Id || saving}
                    className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleCounter(row)}
                    disabled={rowActionId === row.Id || saving}
                    className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                  >
                    {row.IsOpen ? <ToggleLeft className="h-3.5 w-3.5" /> : <ToggleRight className="h-3.5 w-3.5" />}
                    {row.IsOpen ? 'Close' : 'Open'}
                  </button>
                  {row.IsOpen && (
                    <button
                      type="button"
                      onClick={() => saveLastNo(row)}
                      disabled={rowActionId === row.Id || saving}
                      className="inline-flex items-center gap-1 rounded-lg bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50"
                    >
                      <Save className="h-3.5 w-3.5" />
                      Save
                    </button>
                  )}
                </div>
              );
            }
            if (column.field === 'area') {
              return <span className="font-medium text-gray-900">{row.Area?.Name || '-'}</span>;
            }
            return resolveCellValue(row, column.field);
          }}
        />
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 p-5">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-red-100 p-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
              </div>
            </div>
            <div className="p-5 text-sm text-gray-600">
              Click confirm to delete counter for <span className="font-semibold text-gray-900">{deleteTarget.Area?.Name || 'this area'}</span>.
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 p-5">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={saving}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default CounterNos;
